import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Lookups, PageResult, Row } from "../../config/types";
import { api } from "../../services/api";
import { display } from "../../utils/format";
import { Badge, ErrorNotice, Loading, Modal } from "../../components/common/Primitives";
import useGoogleCalendar, { type GoogleEvent } from "../meeting-reservations/useGoogleCalendar";
import { GoogleCalendarEvents, GoogleCalendarEventDetails } from "../../components/common/GoogleCalendarEvents";

function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

type Props = {
  query: string;
  revision: unknown;
  lookups: Lookups;
  canCreate: boolean;
  canEdit: (row: Row) => boolean;
  canDelete: (row: Row) => boolean;
  canComplete: boolean;
  onCreate: (date: string) => void;
  onEdit: (row: Row) => void;
  onDelete: (row: Row) => void;
  onComplete: (row: Row) => void;
};

export default function InterviewCalendar({
  query, revision, lookups, canCreate, canEdit, canDelete, canComplete,
  onCreate, onEdit, onDelete, onComplete,
}: Props) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState<Row>();
  const [selectedGoogle, setSelectedGoogle] = useState<GoogleEvent>();
  const google = useGoogleCalendar(month, revision);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSelected(undefined);
    async function load() {
      const all: Row[] = [];
      let page = 1;
      let total = 0;
      do {
        const params = new URLSearchParams({ q: query, page: String(page++), limit: "100", sort: "date", order: "ASC" });
        const result = await api<PageResult>("/interviews?" + params, { signal: controller.signal });
        all.push(...result.items);
        total = result.total;
        if (!result.items.length) break;
      } while (all.length < total);
      if (!controller.signal.aborted) setRows(all);
    }
    load().catch((cause: Error) => {
      if (!controller.signal.aborted) setError(cause.message);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [query, revision, retry]);

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: Math.ceil((offset + days) / 7) * 7 }, (_, index) =>
    new Date(month.getFullYear(), month.getMonth(), index - offset + 1));
  const locale = i18n.resolvedLanguage || "fr";
  const today = dateKey(new Date());
  const displayedMonth = dateKey(month).slice(0, 7);
  const move = (amount: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + amount, 1));

  return <div className="reservation-calendar interview-calendar">
    {google.error && <ErrorNotice message={t(google.error)} retry={google.refresh} />}
    <div className="calendar-toolbar">
      <div>
        <h3 aria-live="polite">{month.toLocaleDateString(locale, { month: "long", year: "numeric" })}</h3>
        {canCreate && <p>{t("Click a date to create an interview.")}</p>}
      </div>
      <div className="calendar-navigation">
        <button className="button secondary" onClick={() => setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>{t("Today")}</button>
        <button className="icon-button" aria-label={t("Previous month")} onClick={() => move(-1)}><ChevronLeft size={18} /></button>
        <button className="icon-button" aria-label={t("Next month")} onClick={() => move(1)}><ChevronRight size={18} /></button>
      </div>
    </div>
    {error ? <ErrorNotice message={error} retry={() => setRetry(value => value + 1)} /> : loading ? <Loading /> : <>
      <div className="calendar-scroll">
        <div className="calendar-grid">
          {Array.from({ length: 7 }, (_, index) => <div className="calendar-weekday" key={index}>{new Date(2026, 0, 5 + index).toLocaleDateString(locale, { weekday: "short" })}</div>)}
          {cells.map(date => {
            const key = dateKey(date);
            const interviews = rows.filter(row => String(row.date).slice(0, 10) === key).sort((a, b) => String(a.time).localeCompare(String(b.time)));
            const create = () => { if (canCreate) onCreate(key); };
            return <div
              key={key}
              className={"calendar-day" + (canCreate ? " can-create" : "") + (date.getMonth() !== month.getMonth() ? " outside-month" : "") + (key === today ? " is-today" : "")}
              title={canCreate ? t("Create an interview on {{date}}", { date: date.toLocaleDateString(locale, { dateStyle: "long" }) }) : undefined}
              onClick={create}
            >
              {canCreate ? <button
                className="calendar-date-button"
                aria-label={t("Create an interview on {{date}}", { date: date.toLocaleDateString(locale, { dateStyle: "long" }) })}
                onClick={event => { event.stopPropagation(); create(); }}
              ><time dateTime={key}>{date.getDate()}</time></button> : <time dateTime={key} aria-label={date.toLocaleDateString(locale, { dateStyle: "full" })}>{date.getDate()}</time>}
              {interviews.map(row => <button
                key={row.id}
                className={"calendar-booking" + (row.done ? " completed" : "")}
                onClick={event => { event.stopPropagation(); setSelected(row); }}
              >
                {row.done && <Badge>{t("Completed")}</Badge>}
                <strong>{display(row, "time", lookups)}</strong>
                <span>{`${row.firstName} ${row.lastName}`}</span>
                <span>{display(row, "location", lookups)}</span>
              </button>)}
              <GoogleCalendarEvents items={google.items} date={date} query={query} onSelect={setSelectedGoogle} />
            </div>;
          })}
        </div>
      </div>
      {!rows.some(row => String(row.date).startsWith(displayedMonth)) && !google.items.length && <p className="calendar-empty" role="status">{t("No interviews this month.")}</p>}
    </>}
    {selected && <Modal title={`${selected.firstName} ${selected.lastName}`} wide className="reservation-details-modal" onClose={() => setSelected(undefined)}>
      <dl className="calendar-details">
        {[
          ["Date", "date"], ["Time", "time"], ["Department", "departmentId"],
          ["Location", "location"], ["Recruiter", "recruiter"], ["HR manager", "hrManager"],
          ["Technical manager", "technicalManager"], ["Status", "interviewStatus"],
        ].map(([label, key]) => <div key={key}><dt>{t(label)}</dt><dd>{key === "interviewStatus" ? <Badge>{display(selected, key, lookups)}</Badge> : display(selected, key, lookups)}</dd></div>)}
      </dl>
      {(canEdit(selected) || canDelete(selected) || (canComplete && !selected.done)) && <div className="modal-actions">
        {canDelete(selected) && <button className="button destructive" onClick={() => { onDelete(selected); setSelected(undefined); }}>{t("Delete interview")}</button>}
        {canComplete && !selected.done && <button className="button secondary" onClick={() => { onComplete(selected); setSelected(undefined); }}>{t("Mark completed")}</button>}
        {canEdit(selected) && <button className="button primary" onClick={() => { onEdit(selected); setSelected(undefined); }}>{t("Edit interview")}</button>}
      </div>}
    </Modal>}
    {selectedGoogle && <GoogleCalendarEventDetails event={selectedGoogle} onClose={() => setSelectedGoogle(undefined)} note="Your Google events are visible only to you. They do not create an interview." />}
  </div>;
}
